import {
  AutomationActionTemplateGoogleCalendarEventAnchor,
  IAutomationActionTemplateMicrosoftCalendarEventInvitation,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import {
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormHelperText,
  ListSubheader,
  MenuItem,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { capitalize, compact, debounce, isEqual } from 'lodash';
import { Info, RefreshCcw } from 'lucide-react';
import moment from 'moment-timezone';
import { SetStateAction, useEffect, useMemo, useState } from 'react';
import { AutocompleteMultiFreeSolo } from '../../../../../../../components/Autocomplete';
import { WithShape } from '../../../../../../../components/Charts/Util';
import { CustomInputLabel } from '../../../../../../../components/CustomInputLabel';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { LinkExternal } from '../../../../../../../components/LinkExternal';
import { Loader } from '../../../../../../../components/Loader';
import { SelectCustom } from '../../../../../../../components/SelectCustom';
import { useErrorLogger } from '../../../../../../../hooks/useErrorLogger';
import { usePromise } from '../../../../../../../hooks/usePromise';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../../services/currentUser';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import {
  addToTs,
  fromMoment,
  toWeekAndDayDuration
} from '../../../../../../../services/time';
import { getUsersByOrgId } from '../../../../../../../services/user';
import { useTheme } from '../../../../../../../themes';
import {
  getMatchingCalendarEvents,
  useCalendarList
} from '../../../../../../Org/pages/Integrations/integrations/microsoft/calendar';
import { AnchorField } from '../../../../Anchors';
import { Duration } from '../../../TaskBuilder/components/TimeSelectors';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti, actionActorToString } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  useFieldValidator,
  VALIDATORS_MICROSOFT_CALENDAR_EVENT_INVITATION
} from '../../validation';
import { useGoogleCalendarEventInvitiationAnchorOptions } from '../GoogleCalendar';

const HIDE_TOGGLE = true;

const PreviewHint = styled('div')((p) => ({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: p.theme.custom.colors.gray.w400,
  fontSize: p.theme.custom.fontSize.s,
  minHeight: 50
}));

const PreviewHeading = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: p.theme.spacing(0.5),
  gap: p.theme.spacing()
}));

const PreviewEventContainer = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'baseline',
  gap: p.theme.spacing(),
  fontVariantNumeric: 'tabular-nums'
}));

const PreviewEvents = ({
  orgId,
  value,
  onChange
}: {
  orgId: string;
  value: IAutomationActionTemplateMicrosoftCalendarEventInvitation;
  onChange: (
    nextValue: IAutomationActionTemplateMicrosoftCalendarEventInvitation
  ) => void;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const { calendar, query, anchor } = value;
  const ready = calendar.id && value.query;

  const [args, setArgs] = useState(
    ready
      ? {
          orgId,
          calendarId: calendar.id,
          args: {
            q: query,
            minDate: addToTs(
              fromMoment(moment().startOf('d')),
              anchor.lowerBoundary
            ),
            maxResults: 5
          }
        }
      : null
  );

  const debouncedSetArgs = useMemo(() => debounce(setArgs, 500), []);

  useEffect(() => {
    debouncedSetArgs(
      ready
        ? {
            orgId,
            calendarId: calendar.id,
            args: {
              q: query,
              minDate: addToTs(
                fromMoment(moment().startOf('d')),
                anchor.lowerBoundary
              ),
              maxResults: 5
            }
          }
        : null
    );
  }, [orgId, calendar, query, anchor, debouncedSetArgs, ready]);

  const [events, loading, error, forceReload] = usePromise(async () => {
    if (!args) {
      return [];
    }
    return getMatchingCalendarEvents(args.orgId, args.calendarId, args.args);
  }, [args]);

  useErrorLogger('match MS calendar events', error);

  useEffect(() => {
    if (events && !!events.length) {
      const nextValue = { ...value, eventId: events[0].eventId };
      onChange(nextValue);
    }
  }, [events]);

  return (
    <div>
      <PreviewHeading>
        <Tooltip
          title={`${t(
            'This is a sample list. Employees will be invited to the first event that matches once they are enrolled in the experience'
          )}`}
        >
          <FlexContainer spacing={0.5}>
            <Typography variant="h6">{t('Sample events')}</Typography>
            <Info size={16} color={theme.custom.colors.gray.w500} />
          </FlexContainer>
        </Tooltip>
        {!loading && ready && (
          <Button
            onClick={forceReload}
            size="small"
            startIcon={<RefreshCcw size={14} />}
          >
            Refresh
          </Button>
        )}
      </PreviewHeading>
      {loading && <Loader height={150} size={16} />}
      {!ready && (
        <PreviewHint>
          {t('Choose a calendar and an event name to preview matching events.')}
        </PreviewHint>
      )}

      {!loading &&
        ready &&
        events &&
        !!events.length &&
        events.map((ev) => (
          <div key={ev.eventId}>
            <LinkExternal color="primary" href={ev.htmlLink}>
              <PreviewEventContainer>
                <Typography variant="caption">
                  {moment(ev.startTime).format('MM/DD/YYYY')}
                </Typography>
                <div>{ev.eventName}</div>
              </PreviewEventContainer>
            </LinkExternal>
          </div>
        ))}
      {!loading && ready && events && !events.length && (
        <PreviewHint>{t('No matching events found.')}</PreviewHint>
      )}
    </div>
  );
};

const AnchorSelector = ({
  value,
  onChange,
  expTpl,
  fullWidth,
  anchorFields,
  error,
  onBlur
}: {
  value: AutomationActionTemplateGoogleCalendarEventAnchor;
  onChange: (
    nextAnchor: AutomationActionTemplateGoogleCalendarEventAnchor
  ) => void;
  expTpl: TaskBuilderContext;
  fullWidth?: boolean;
  error?: boolean;
  anchorFields: AnchorField[];
  onBlur: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const options = useGoogleCalendarEventInvitiationAnchorOptions(
    expTpl.anchor,
    anchorFields
  );

  return (
    <>
      <FormRow>
        <SelectCustom
          label={t('Find the first event')}
          fullWidth={fullWidth}
          value={value.type}
          onChange={(ev) => {
            const nextType = ev.target
              .value as AutomationActionTemplateGoogleCalendarEventAnchor['type'];
            const nextValue = options.find((o) => o.type === nextType)?.data;
            if (nextValue) {
              onChange({
                ...nextValue,
                lowerBoundary: value.lowerBoundary
              });
            }
          }}
        >
          {options.map((o) => (
            <MenuItem key={o.type} value={o.type} disabled={o.disabled}>
              {capitalize(o.label)}
            </MenuItem>
          ))}
        </SelectCustom>
      </FormRow>
      {(value.type === 'AFTER_AUTOMATION' ||
        value.type === 'BEFORE_MILESTONE' ||
        value.type === 'AFTER_MILESTONE' ||
        value.type === 'AFTER_EXPERIENCE_START_DATE') && (
        <FormRow>
          <CustomInputLabel label={t('Skip the first')} variant="secondary">
            <Duration
              onBlur={onBlur}
              value={value.lowerBoundary}
              onChange={(nextLowerBoundary) => {
                onChange({
                  ...value,
                  lowerBoundary: nextLowerBoundary
                });
              }}
              error={error}
              zeroBased
            />
          </CustomInputLabel>
        </FormRow>
      )}
    </>
  );
};

const CalendarSelector = ({
  value,
  onChange,
  fullWidth,
  invalidReason,
  onBlur
}: {
  value: { id: string; name: string; color?: string };
  onChange: (nextValue: { id: string; name: string }) => void;
  fullWidth?: boolean;
  invalidReason: string | null;
  onBlur: () => void;
}) => {
  // TODO load options asynchronously
  const { org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  const [calendars, loading, error] = useCalendarList(org.id);

  useErrorLogger('calendar list error', error);

  const options = useMemo(() => {
    if (!calendars) {
      return [value].filter((v) => !!v.id);
    }
    let addCurrentValueAfterwards = true;
    const opts = calendars.map((c) => {
      if (value.id === c.id) {
        addCurrentValueAfterwards = false;
      }
      return { id: c.id, name: c.name, color: c.color };
    });
    return (addCurrentValueAfterwards ? [value, ...opts] : opts).filter(
      (v) => !!v.id
    );
  }, [value, calendars]);

  const sortedOptions = options.sort(function (a, b) {
    var nameA = a.name.toUpperCase(); // ignore upper and lowercase
    var nameB = b.name.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    // names must be equal
    return 0;
  });
  return (
    <SelectCustom
      label={t('In calendar')}
      value={value.id || ''}
      onChange={(ev) => {
        const nextId = ev.target.value as string;
        const nextOption = sortedOptions.find((o) => o.id === nextId);
        nextOption && onChange(nextOption);
      }}
      renderInitialValueImmediately
      loading={loading}
      fullWidth={fullWidth}
      error={!!invalidReason}
      helperText={invalidReason}
      onBlur={onBlur}
    >
      <ListSubheader>{t('Available calendars')}</ListSubheader>
      {sortedOptions.map((o) => (
        <MenuItem key={o.id} value={o.id}>
          <WithShape shape="circle" color={o.color || '#efefef'}>
            {o.name}
          </WithShape>
        </MenuItem>
      ))}
    </SelectCustom>
  );
};

export const ActionSettingsMicrosoftCalendarEventInvitation = ({
  value,
  automation,
  onChange,
  expTpl,
  validation,
  setValidation,
  anchorFields
}: {
  value: IAutomationActionTemplateMicrosoftCalendarEventInvitation;
  automation: IAutomationTemplate;
  onChange: (
    nextValue: IAutomationActionTemplateMicrosoftCalendarEventInvitation
  ) => void;
  expTpl: TaskBuilderContext;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  anchorFields: AnchorField[];
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();

  const validateQuery = useFieldValidator(
    setValidation,
    VALIDATORS_MICROSOFT_CALENDAR_EVENT_INVITATION,
    'query'
  );
  const invalidQuery = validation?.fields.query || null;

  const validateCalendar = useFieldValidator(
    setValidation,
    VALIDATORS_MICROSOFT_CALENDAR_EVENT_INVITATION,
    'calendar'
  );
  const invalidCalendar = validation?.fields.calendar || null;

  const validatePplToAdd = useFieldValidator(
    setValidation,
    VALIDATORS_MICROSOFT_CALENDAR_EVENT_INVITATION,
    'pplToAdd'
  );

  const validateAnchor = useFieldValidator(
    setValidation,
    VALIDATORS_MICROSOFT_CALENDAR_EVENT_INVITATION,
    'anchor'
  );

  const invalidPpltoAdd = validation?.fields.pplToAdd || null;
  const invalidAnchor = validation?.fields.anchor || null;

  const optionalInvitees = value.pplToAdd.filter(
    (p) => p.type === 'actor' && p.optional
  );
  const mandatoryInvitees = value.pplToAdd.filter(
    (p) => p.type === 'actor' && !p.optional
  );
  const actorInvitees = value.pplToAdd.filter((x) => x.type === 'actor');

  return (
    <div>
      <FormRow>
        <ActionActorSelectorMulti
          label={t('Mandatory Invitees')}
          value={mandatoryInvitees}
          onChange={(nextPplToAdd) => {
            // artificially split to only contain actors, so we need to recompose
            const nextValue = {
              ...value,
              pplToAdd: nextPplToAdd.concat(
                value.pplToAdd.filter(
                  (p) =>
                    p.type === 'other' || (p.type === 'actor' && p.optional)
                )
              )
            };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, automation);
            return onChange(nextValue);
          }}
          expTpl={expTpl}
          fullWidth
          error={!!invalidPpltoAdd}
          helperText={invalidPpltoAdd}
          onBlur={() => validatePplToAdd(value, expTpl, automation)}
          filterOptions={(options) => {
            return options.filter(
              ({ optional, ...optionWithoutOptional }) =>
                !actorInvitees.some(({ optional, ...actorWithoutOptional }) =>
                  isEqual(optionWithoutOptional, actorWithoutOptional)
                )
            );
          }}
        />
      </FormRow>

      <FormRow>
        <ActionActorSelectorMulti
          label={t('Optional Invitees')}
          value={optionalInvitees}
          onChange={(nextPplToAdd) => {
            // artificially split to only contain actors, so we need to recompose
            nextPplToAdd.forEach((p) => (p.optional = true));
            const nextValue = {
              ...value,
              pplToAdd: nextPplToAdd.concat(
                value.pplToAdd.filter(
                  (p) =>
                    p.type === 'other' || (p.type === 'actor' && !p.optional)
                )
              )
            };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, automation);
            return onChange(nextValue);
          }}
          expTpl={expTpl}
          fullWidth
          onBlur={() => validatePplToAdd(value, expTpl, automation)}
          filterOptions={(options) => {
            return options.filter(
              (o) => !actorInvitees.some((ppl) => isEqual(ppl, o))
            );
          }}
        />
      </FormRow>

      <FormRow>
        <AutocompleteMultiFreeSolo
          label={t('Additional people to invite (by email address)')}
          fullWidth
          value={compact(
            value.pplToAdd.map((p) => (p.type === 'other' ? p.email : null))
          )}
          onChange={(nextEmails) => {
            const nextPplToAdd = value.pplToAdd
              .filter((p) => p.type === 'actor')
              .concat(
                nextEmails.map((email) => ({
                  type: 'other',
                  email: email.trim(),
                  optional: false
                }))
              );
            const nextValue = { ...value, pplToAdd: nextPplToAdd };
            invalidPpltoAdd && validatePplToAdd(nextValue, expTpl, automation);
            return onChange(nextValue);
          }}
          options={() =>
            getUsersByOrgId(expTpl.orgId).then((us) =>
              us.map((u) => u.data.email).filter((t) => t)
            )
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="default"
                size="small"
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
          onBlur={() => validatePplToAdd(value, expTpl, automation)}
        />
      </FormRow>

      <FormRow>
        <TextField
          label={t('Look for event with name')}
          value={value.query}
          error={!!invalidQuery}
          helperText={invalidQuery}
          onBlur={() => validateQuery(value, expTpl, automation)}
          onChange={(ev) => {
            const nextValue = { ...value, query: ev.target.value };
            invalidQuery && validateQuery(nextValue, expTpl, automation);
            return onChange(nextValue);
          }}
          fullWidth
        />
      </FormRow>
      <FormRow>
        <CalendarSelector
          value={value.calendar}
          onChange={(nextCalendar) => {
            const nextValue = { ...value, calendar: nextCalendar };
            invalidCalendar && validateCalendar(nextValue, expTpl, automation);
            return onChange(nextValue);
          }}
          fullWidth
          invalidReason={invalidCalendar}
          onBlur={() => validateCalendar(value, expTpl, automation)}
        />
      </FormRow>

      <AnchorSelector
        value={value.anchor}
        onChange={(nextAnchor) => {
          const nextValue = { ...value, anchor: nextAnchor };
          invalidAnchor && validateAnchor(value, expTpl, automation);
          return onChange(nextValue);
        }}
        expTpl={expTpl}
        error={!!invalidAnchor}
        fullWidth
        onBlur={() => validateAnchor(value, expTpl, automation)}
        anchorFields={anchorFields}
      />

      <FormHelperText error={!!invalidAnchor}>{invalidAnchor}</FormHelperText>
      {!HIDE_TOGGLE && (
        <FormRow>
          <FormControlLabel
            label={
              <FlexContainer alignItems="center" justifyContent="center">
                {t('Add to recurring events')}
                <Tooltip
                  title={`${t(
                    'If toggled employees will be added to all recurring events, past events included.'
                  )}`}
                >
                  <Info size={16} color={theme.custom.colors.gray.w500} />
                </Tooltip>
              </FlexContainer>
            }
            control={
              <Checkbox
                checked={value.isRecurring}
                onChange={(ev) => {
                  const nextValue = {
                    ...value,
                    isRecurring: ev.target.checked
                  };
                  return onChange(nextValue);
                }}
              />
            }
          />
        </FormRow>
      )}

      <PreviewEvents orgId={expTpl.orgId} value={value} onChange={onChange} />
    </div>
  );
};

const AnchorSelectorReadOnly = ({
  value,
  expTpl,
  anchorFields
}: {
  value: AutomationActionTemplateGoogleCalendarEventAnchor;
  expTpl: TaskBuilderContext;
  anchorFields: AnchorField[];
  fullWidth?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const options = useGoogleCalendarEventInvitiationAnchorOptions(
    expTpl.anchor,
    anchorFields
  );
  return (
    <>
      <FormRow>
        <DataFieldWithLabel label={t('Find the first event')}>
          {capitalize(options.find((o) => o.type === value.type)?.label || '')}
        </DataFieldWithLabel>
      </FormRow>

      {(value.type === 'AFTER_AUTOMATION' ||
        value.type === 'AFTER_EXPERIENCE_START_DATE' ||
        value.type === 'BEFORE_MILESTONE' ||
        value.type === 'AFTER_MILESTONE') && (
        <FormRow>
          <DataFieldWithLabel label={t('Skip the first')}>
            {toWeekAndDayDuration(value.lowerBoundary, t) || `0 ${t('days')}`}
          </DataFieldWithLabel>
        </FormRow>
      )}
    </>
  );
};

export const ActionSettingsMicrosoftCalendarEventInvitationReadOnly = ({
  value,
  expTpl,
  anchorFields
}: {
  value: IAutomationActionTemplateMicrosoftCalendarEventInvitation;
  expTpl: TaskBuilderContext;
  anchorFields: AnchorField[];
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Who should be invited')}>
          {joinWithCommaAndAnd(
            value.pplToAdd.map((p) =>
              actionActorToString(p, expTpl.supportingActors, t)
            ),
            t
          )}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Look for event with name')}>
          {value.query}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('In calendar')}>
          {value.calendar.name}
        </DataFieldWithLabel>
      </FormRow>

      <AnchorSelectorReadOnly
        value={value.anchor}
        expTpl={expTpl}
        anchorFields={anchorFields}
        fullWidth
      />
    </div>
  );
};
